import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { ITrailer } from '../../types/trailer';
import { ISelectOption } from '../../types/list';
import { IVendorLight } from '../../types/vendor';

import { useListContext } from '../../contexts/listContext';
import { useUserContext } from '../../contexts/userContext';

import { EmployeeService } from '../../services/employeeService';
import { VendorService } from '../../services/vendorService';
import { TransporterService } from '../../services/transporterService';
import { UtilityService } from '../../services/utilityService';
import { TrailerService } from '../../services/trailerService';
import { CustomerService } from '../../services/customerService';

import Json from '../json';

import '../../styles/trailerDetailsForm.scss';

export interface ITrailerDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: ITrailer;
	reloadData: () => void;
}

const TrailerDetailsForm: React.FunctionComponent<ITrailerDetailsForm> = ({ action, id, details, reloadData }) => {
	const listContext = useListContext();
	const userContext = useUserContext();
	const navigate = useNavigate();
	const [formFields, setFormFields] = useState<ITrailer>(details);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
	const [isLoadingVendors, setIsLoadingVendors] = useState(true);
	const [isLoadingTransporters, setIsLoadingTransporters] = useState(true);

	const [customerOptions, setCustomerOptions] = useState<ISelectOption[]>([]);
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [transporterOptions, setTransporterOptions] = useState<ISelectOption[]>([]);

	useEffect(() => {
		getCustomerOptions();
	}, [])

	const getCustomerOptions = async () => {
		setIsLoadingCustomers(true);
		const allCustomers = CustomerService.mapCustomerLightList((await CustomerService.getCustomers())?.data);

		// Update the logged in user's TILS access list
		EmployeeService.getCurrentUser().then((response) => {
			userContext.tilsAccess = response.data.tilsAccess;

			let options: ISelectOption[] = [];
			userContext.tilsAccess?.map((item) => {
				options.push({
					"name": item.customerName,
					"value": item.customerId.toString(),
					"isActive": allCustomers.some(c => +c.id == item.customerId && c.status == "Active")
				})
			})
			options = options.filter(o => o.isActive);
			setCustomerOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

			if (options.length > 0) {
				setFormFields(prev => ({ ...prev, ["customerId"]: parseInt(options[0].value) }))
			}
		})
		.catch((error) => {
			console.log('EmployeeService.getCurrentUser: ERROR', error);
			setErrorMessage("We encountered an error while processing your request. Please try again.");
		}).finally(() => {
			setIsSaving(false);
			setIsLoadingCustomers(false);
		});		
	}

	useEffect(() => {
		getVendorOptions();
	}, [formFields.customerId])

	const getVendorOptions = () => {
		setIsLoadingVendors(true);
		VendorService.getVendorsByCustomerId(formFields.customerId)
			.then((response) => {
				console.log('VendorService.getVendorsByCustomerId: SUCCESS', response);
				let options: ISelectOption[] = [];
				response.data.map((item: IVendorLight) => {
					options.push({
						"name": item.name,
						"value": item.id,
						"isActive": item.status == 'Active'
					})
				})
				setVendorOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
				
				if (options.length > 0) {
					// If the previously selected vendor is in this list, just leave it selected
					if (!options.some(v => +v.value == formFields.vendorId)) {
						setFormFields(prev => ({ ...prev, ["vendorId"]: parseInt(options[0].value) }));
					}
				}
				else {
					setFormFields(prev => ({ ...prev, ["vendorId"]: -1 }))
				}
			})
			.catch((error) => {
				console.log('VendorService.getVendorsByCustomerId: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);
				setIsLoadingVendors(false);
			});
	}

	useEffect(() => {
		getTransporterOptions();
	}, [formFields.vendorId])

	const getTransporterOptions = () => {
		if (formFields.vendorId == -1) return;
		setIsLoadingTransporters(true);
		TransporterService.getAllByVendor(formFields.vendorId)
			.then((response) => {
				console.log('TransporterService.getAllByVendor: SUCCESS', response);
				let options: ISelectOption[] = [];
				response.data.map((item: IVendorLight) => {
					options.push({
						"name": item.name,
						"value": item.id,
						"isActive": item.status == 'Active'
					})
				})
				setTransporterOptions(options.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));

				if (options.length > 0) {
					// If the previously selected transporter is in this list, just leave it selected
					if (!options.some(v => +v.value == formFields.transporterId)) {
						setFormFields(prev => ({ ...prev, ["transporterId"]: parseInt(options[0].value) }));
					}					
				}
				else {
					setFormFields(prev => ({ ...prev, ["transporterId"]: -1 }));
				}
			})
			.catch((error) => {
				console.log('TransporterService.getAllByVendor: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);
				setIsLoadingTransporters(false);
			});
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage(undefined);
		setIsSaving(true);

		if (action == "add") {
			TrailerService.addTrailer(formFields)
				.then((response) => {
					console.log('TrailerService.addTrailer: SUCCESS', response);
					navigate("/trailer");
				})
				.catch((error) => {
					console.log('TrailerService.addTrailer: ERROR', error);
					setErrorMessage("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsSaving(false);
				});
		}
		else if (action == "edit" && id) {
			// TrailerService.setDetails(id, formFields)
			// 	.then((response) => {
			// 		console.log('TrailerService.setDetails: SUCCESS', response);
			// 		reloadData();
			// 	})
			// 	.catch((error) => {
			// 		console.log('TrailerService.setDetails: ERROR', error);
			// 		setErrorMessage("We encountered an error while processing your request. If the problem persists, please contact your administrator.");
			// 	})
			// 	.finally(() => {
			// 		setIsSaving(false);
			// 	});
		}
	}

	return (
		<section className="hwi-trailer-details-form">
			{errorMessage &&
				<Alert className="alert-error"
					severity="error"
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
					{errorMessage}
				</Alert>
			}
			<Form onSubmit={(event) => handleSubmit(event)}>
				<div className="row">
					<InputLabel>{isLoadingCustomers ? "Loading Customers..." : "Customer *"}</InputLabel>
					<Select
						value={formFields.customerId}
						required
						className="customer-field"
						onChange={(event) => { setFormFields({ ...formFields, ["customerId"]: parseInt(event.target.value.toString()) }) }}
					>
						{customerOptions.filter(c => c.isActive).map((item, index) => (
							<MenuItem value={Number.parseInt(item.value)} key={index}>{item.name}</MenuItem>
						))}
					</Select>
				</div>
				<div className="row">
					<InputLabel>{isLoadingCustomers || isLoadingVendors ? "Loading Vendors..." : "Vendor *"}</InputLabel>
					<Select
						value={formFields.vendorId == -1 || isLoadingVendors ? '' : formFields.vendorId}
						required
						className="vendor-field"
						onChange={(event) => { setFormFields({ ...formFields, ["vendorId"]: parseInt(event.target.value.toString()) }) }}
						disabled={isLoadingVendors}
					>
						{vendorOptions.filter(v => v.isActive).map((item, index) => (
							<MenuItem value={Number.parseInt(item.value)} key={index}>{item.name}</MenuItem>
						))}
					</Select>
				</div>
				<div className="row">
				<InputLabel>{isLoadingCustomers || isLoadingVendors || isLoadingTransporters ? "Loading Transporters..." : "Transporter *"}</InputLabel>
					<Select
						value={formFields.transporterId == -1 || isLoadingVendors || isLoadingTransporters ? '' : formFields.transporterId}
						className="transporter-field"
						required
						onChange={(event) => { setFormFields({ ...formFields, ["transporterId"]: parseInt(event.target.value.toString()) }) }}
						disabled={isLoadingVendors || isLoadingTransporters}
					>
						{transporterOptions.filter(t => t.isActive).map((item, index) => (
							<MenuItem value={Number.parseInt(item.value)} key={index}>{item.name}</MenuItem>
						))}
					</Select>
				</div>
				<div className="row">
					<InputLabel>Trailer Type *</InputLabel>
					<Select
						value={formFields.trailerTypeId}
						required
						className="trailer-type-field"
						onChange={(event) => { setFormFields({ ...formFields, ["trailerTypeId"]: parseInt(event.target.value.toString()) }) }}
					>
						{listContext.trailerTypes.map((item, index) => (
							<MenuItem value={Number.parseInt(item.value)} key={index}>{item.name}</MenuItem>
						))}
					</Select>
				</div>
				<div className="row">
					<InputLabel>Trailer Number *</InputLabel>
					<TextField className="trailer-number-field" required autoFocus name="trailerNumber" value={formFields.trailerNumber} size="small" onChange={(event) => { setFormFields({ ...formFields, ["trailerNumber"]: event.target.value }) }}></TextField>
				</div>
				<div className="row">
					<InputLabel>Location</InputLabel>
					<TextField className="location-field" name="location" value={formFields.location} size="small" onChange={(event) => { setFormFields({ ...formFields, ["location"]: event.target.value }) }}></TextField>
				</div>
				<div className="row controls">
					<Button type="submit" className="save-button" disabled={isSaving} variant="contained">{isSaving ? "Saving..." : "Save Trailer"}</Button>
				</div>
			</Form>
			{/* <Json data={formFields}></Json> */}
		</section>
	)
}

export default TrailerDetailsForm;